import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  DeleteConfirmationModal,
  SessionExpirationModal,
} from '../../common/Modals/MainMap/MainMapModal';
import PointLayerMenu from '../IconTool/LayerPanel/PointLayerMenu';
import IconTool from '../IconTool/PointPanel/IconTool';
import LayerOverview from '../Legend/LayerOverview';
import Map from '../Map/Map';
import ResultPanel from '../ResultPanel/ResultPanel';

import { useSessionManager } from '../../../hooks/useSessionManager';
import { useMeshLayerQuery } from '../../../queries/useMeshLayerQuery';
import { useInitPoint } from '../../../queries/usePointsQuery';
import { useInitStaticLayers } from '../../../queries/useStaticLayerQuery';
import { useMapLayers } from '../Map/hooks/useMapLayers';
import { useMapPopups } from '../Map/hooks/useMapPopups';

import distance from '@turf/distance';
import { point as turfPoint } from '@turf/helpers';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import useAuthStore from '../../../store/authStore';
import useCartStore from '../../../store/cartStore';
import useDatasetStore from '../../../store/datasetStore';
import useHomeStore from '../../../store/homeStore';
import { useMapLayerStore } from '../../../store/layerStore';
import useLegendStore from '../../../store/legendStore';
import useMapStore from '../../../store/mapStore';
import { useActiveMarker, useMarkerStore } from '../../../store/markerStore';
import { DatasetType } from '../../../types/marker';
import { SEARCH_PARAM } from '../../../utils/common';
import { decodeInLoop } from '../../../utils/decodeInLoop';
import { withLoading } from '../../common/Loading/withLoading';
import { useMapMeasurement } from '../Map/hooks/useMapMeasurement';
import styles from './AppContainer.module.scss';
import { parseUrlCoordinates } from './utils/parseUrlCoordinates';

export default function AppContainer() {
  const user = useAuthStore((state) => state.user);

  const showMesh = useLegendStore((state) => state.showMesh);

  const isMeasureMode = useLegendStore((state) => state.isMeasureMode);
  const zoom = useMapStore((state) => state.viewState.zoom);

  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);

  const datasetZones = useMapLayerStore((state) => state.datasetZones);

  const shoppingItems = useCartStore((state) => state.shoppingItems);

  const coordsMoved = useMapStore((state) => state.coordsMoved);
  const setCoordsMoved = useMapStore((state) => state.setCoordsMoved);

  const showResultCollapse = useHomeStore((state) => state.showResultCollapse);
  const setShowResultCollapse = useHomeStore(
    (state) => state.setShowResultCollapse
  );

  const markers = useMarkerStore((state) => state.markers);
  const activeMarker = useActiveMarker();
  const setActiveMarker = useMarkerStore((state) => state.setActiveMarker);
  const updateMarker = useMarkerStore((state) => state.updateMarker);
  const isAddMarkerMode = useMarkerStore((state) => state.isAddMarkerMode);
  const setAddMarkerMode = useMarkerStore((state) => state.setAddMarkerMode);
  const setActivePanel = useMarkerStore((state) => state.setActivePanel);
  const setActiveDatasetKey = useMarkerStore(
    (state) => state.setActiveDatasetKey
  );

  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [legendStartEndValues, setLegendStartEndValues] = useState<
    [number | null, number | null]
  >([null, null]);

  const loadingContext = useContext(LoadingContext);
  const location = useLocation();
  const didMountRef = useRef(false);
  const pendingActivePanelRef = useRef<string | null>(null);
  const point = activeMarker;

  useInitStaticLayers();
  useInitPoint();
  const { meshUrl, setMeshUrl, meshResults, setMeshResults } =
    useMeshLayerQuery(selectedDatasetId, showMesh);

  const { isModalOpen, handleLogin } = useSessionManager(user);
  const {
    confirmationDeletePopup,
    setConfirmationDeletePopup,
    notAuthorizedUserPopup,
    setNotAuthorizedUserPopup,
    confirmationDeletePopupInsitu,
    setConfirmationDeletePopupInsitu,
    loadingDelete,
    expandedPoints,
    handleDeleteDataset,
    handleDeleteDatasetInsitu,
    handleRequestDeleteDataset,
    handlePopupCoordinatesChanged,
    handleClosePopup,
    popupContent,
    activePopupCoordinates,
    triggerMapClick,
  } = useMapPopups(shoppingItems, setMeshUrl);

  const {
    measurement,
    addMeasurementPoint,
    setDraftPoint,
    setIsSnapToFirst,
    closeMeasurementPolygon,
    startDraggingMeasurementPoint,
    updateMeasurementPoint,
    stopDraggingMeasurementPoint,
    clearMeasurement,
  } = useMapMeasurement();

  const { layer } = useMapLayers(
    meshUrl,
    setMeshUrl,
    expandedPoints,
    isMeasureMode ? measurement : undefined,
    startDraggingMeasurementPoint,
    updateMeasurementPoint,
    stopDraggingMeasurementPoint
  );

  const handleMapClick = useCallback(
    (coordinate: number[]) => {
      if (isMeasureMode && coordinate?.length === 2) {
        if (measurement.isSnapToFirst && measurement.points.length >= 3) {
          closeMeasurementPolygon();
        } else {
          addMeasurementPoint([coordinate[0], coordinate[1]]);
        }
        return;
      }
      triggerMapClick(coordinate, undefined);
    },
    [
      isMeasureMode,
      measurement.isSnapToFirst,
      measurement.points.length,
      closeMeasurementPolygon,
      addMeasurementPoint,
      triggerMapClick,
    ]
  );

  const handleMapMove = useCallback(
    (coords: [number, number]) => {
      if (!isMeasureMode) return;
      if (measurement.draggedPointIndex !== null) return;
      if (measurement.isClosed) return;
      setDraftPoint(coords);

      if (measurement.points.length >= 3) {
        const snapThresholdKm = Math.min(2790 / Math.pow(2, zoom), 100);
        const distToFirst = distance(
          turfPoint(measurement.points[0]),
          turfPoint(coords),
          { units: 'kilometers' }
        );
        setIsSnapToFirst(distToFirst <= snapThresholdKm);
      } else if (measurement.isSnapToFirst) {
        setIsSnapToFirst(false);
      }
    },
    [
      isMeasureMode,
      setDraftPoint,
      setIsSnapToFirst,
      measurement.draggedPointIndex,
      measurement.points,
      measurement.isClosed,
      measurement.isSnapToFirst,
      zoom,
    ]
  );

  const toggleResultCollapse = useCallback(
    (bool: boolean, _checked: boolean = true) => {
      setShowResultCollapse(!bool);
    },
    [setShowResultCollapse]
  );

  const onResetCoordsMoved = useCallback(() => {
    setCoordsMoved(false);
    if (point) {
      updateMarker(point.id, { coordinatesToMove: undefined });
    }
  }, [point, setCoordsMoved, updateMarker]);

  const onAddCoords = useCallback(
    (coords: number[], fwePointName?: string) => {
      withLoading(() => triggerMapClick(coords, fwePointName), loadingContext, {
        source: 'onAddCoords',
      });
    },
    [triggerMapClick, loadingContext]
  );

  const resolvedPopupContent = useMemo(() => {
    if (!activePopupCoordinates()) return null;
    return popupContent();
  }, [popupContent, activePopupCoordinates]);

  const activeCoordinates = useMemo(() => {
    return activePopupCoordinates();
  }, [activePopupCoordinates]);

  const handleInitialActiveMarker = useCallback(
    (fwePointName: string) => {
      const existingMarker = markers.find(
        (marker) => marker.fwePointName === fwePointName
      );
      if (existingMarker) {
        setActiveMarker(existingMarker.id);
      }
    },
    [markers, setActiveMarker]
  );

  useEffect(() => {
    const pendingPanel = pendingActivePanelRef.current;
    if (!pendingPanel || !activeMarker) return;

    setActivePanel(activeMarker.id, pendingPanel);

    if (
      pendingPanel === DatasetType.SW ||
      pendingPanel === DatasetType.HD ||
      pendingPanel === DatasetType.Wind
    ) {
      const selectedDataset =
        activeMarker.downloadPage.find(
          (downloadItem) => downloadItem.radioKey === pendingPanel
        )?.datasetId ?? activeMarker.dataset;

      setActiveDatasetKey(activeMarker.id, pendingPanel, selectedDataset);
    }

    pendingActivePanelRef.current = null;
  }, [activeMarker, setActivePanel, setActiveDatasetKey]);

  useEffect(() => {
    if (!isMeasureMode) {
      clearMeasurement();
    }
  }, [isMeasureMode, clearMeasurement]);

  // Update result panel collapse based on markers or add marker mode
  useEffect(() => {
    setShowResultCollapse(isAddMarkerMode || markers?.length > 0);
  }, [markers?.length, isAddMarkerMode, setShowResultCollapse]);

  // Handle URL parameters for initial coordinates
  useEffect(() => {
    if (didMountRef.current || !datasetBriefs.length) return;

    const searchParams = new URLSearchParams(location.search);
    const coordinate = searchParams.get(SEARCH_PARAM.COOR);

    if (coordinate) {
      const rawFwePointName = searchParams.get(SEARCH_PARAM.FWEPOINTNAME);
      const initialActivePanelFromUrl = searchParams.get(
        SEARCH_PARAM.ACTIVE_PANEL
      );

      if (initialActivePanelFromUrl) {
        pendingActivePanelRef.current = initialActivePanelFromUrl;
      }
      const fwePointName = rawFwePointName
        ? decodeInLoop(rawFwePointName)
        : undefined;

      if (fwePointName && markers.length > 0) {
        handleInitialActiveMarker(fwePointName);
      }

      if (markers.length === 0) {
        const parsedCoordinates = parseUrlCoordinates(coordinate);

        parsedCoordinates.forEach((coords, index) => {
          onAddCoords(coords, index === 0 ? fwePointName : undefined);
        });
      }
    }

    didMountRef.current = true;
  }, [
    datasetBriefs,
    location.search,
    markers,
    onAddCoords,
    handleInitialActiveMarker,
  ]);

  return (
    <div className={styles.map}>
      {datasetZones?.length > 0 && (
        <PointLayerMenu>
          <LayerOverview
            legendStartEndValues={legendStartEndValues}
            // setLegendStartEndValues={setLegendStartEndValues}
            onMeshControl={setSelectedDatasetId}
            result={meshResults}
            setResult={setMeshResults}
          />
        </PointLayerMenu>
      )}

      <IconTool
        legendStartEndValues={legendStartEndValues}
        setLegendStartEndValues={setLegendStartEndValues}
        collapseOpen={!showResultCollapse}
        onCollapse={toggleResultCollapse}
        result={meshResults}
        setResult={setMeshResults}
        data-testid='AppContainer-Collapse_Result_Panel_Button'
      />

      <ResultPanel
        coordsMoved={coordsMoved}
        onResetCoordsMove={onResetCoordsMoved}
        isAddMarkerMode={isAddMarkerMode}
        open={showResultCollapse}
        onMeshControl={setSelectedDatasetId}
        addCoords={onAddCoords}
        onMark={setAddMarkerMode}
        setMeshUrl={setMeshUrl}
      />

      <Map
        onClick={handleMapClick}
        layers={layer}
        popupContent={resolvedPopupContent}
        popupCoordinates={activeCoordinates}
        onPopupCoordinatesChanged={handlePopupCoordinatesChanged}
        onClosePopup={handleClosePopup}
        onMoveMouse={handleMapMove}
        addMeasurementPoint={addMeasurementPoint}
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopup}
        onConfirm={handleDeleteDataset}
        onCancel={() => setConfirmationDeletePopup(false)}
        loading={loadingDelete}
        message='On confirmation the dataset will be removed from MOOD'
      />

      <DeleteConfirmationModal
        visible={notAuthorizedUserPopup}
        onConfirm={handleRequestDeleteDataset}
        onCancel={() => setNotAuthorizedUserPopup(false)}
        loading={loadingDelete}
        message='MIKE MS datasets can only be removed by the project owners, request sent to owners'
      />

      <DeleteConfirmationModal
        visible={confirmationDeletePopupInsitu}
        onConfirm={handleDeleteDatasetInsitu}
        onCancel={() => setConfirmationDeletePopupInsitu(false)}
        loading={loadingDelete}
        message='On confirmation the dataset will be removed from MOOD'
      />

      <SessionExpirationModal visible={isModalOpen} onLogin={handleLogin} />
    </div>
  );
}
